import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthToken } from './useAuthToken'
import { CLOUD_API, paymentLabel } from './shared'
import { money } from '@core/utils/format'
import { exportWorkbook } from '@utils/excel/ExcelExport'
import { deviceLabel } from '@core/utils/deviceLabel'
import { httpErrorText } from '@core/utils/httpError'
import {
  btn,
  btnBase,
  btnSize,
  cx,
  fieldInput,
  fieldLabel,
  hint,
  modalSm,
  overlay,
  table,
  tableWrap,
  td,
  tdNum,
  th,
  thNum
} from '@ui/classes'
import { credInput, errBanner, okBanner } from '@ui/admin'
import { ChevronDown, Close } from '@ui/icons'

const RECEIPTS_PAGE_SIZE = 200
const RECEIPTS_EXPORT_MAX = 20000

/** Человекочитаемые стадии печати чека на проверке. */
const PRINT_STATE_LABELS: Record<string, string> = {
  ready: 'готова',
  failed: 'ошибка',
  printed: 'напечатан',
  cancelled: 'отменён'
}

interface ReceiptFilters {
  returnsOnly: boolean
  nonFiscalOnly: boolean
  markingSkippedOnly: boolean
  czStatus: string
  markingDecision: string
  markingPrintState: string
  onlyApplyErrors: boolean
  onlyNonFiscalMarked: boolean
}

function buildReceiptQuery(
  filters: ReceiptFilters,
  marking: string,
  limit: number,
  offset: number
): URLSearchParams {
  const params = new URLSearchParams()
  const q = marking.trim()
  if (q) params.set('marking_code', q)
  if (filters.returnsOnly) params.set('settlement_type', 'income_return')
  if (filters.nonFiscalOnly) params.set('status', 'non_fiscal')
  if (filters.markingSkippedOnly) params.set('marking_skipped', '1')
  if (filters.czStatus) params.set('cz_status', filters.czStatus)
  if (filters.markingDecision) params.set('marking_decision', filters.markingDecision)
  if (filters.markingPrintState) {
    params.set('marking_print_state', filters.markingPrintState)
  }
  if (filters.onlyApplyErrors) params.set('marking_apply_error', '1')
  if (filters.onlyNonFiscalMarked) params.set('non_fiscal_marked', '1')
  params.set('limit', String(limit))
  if (offset > 0) params.set('offset', String(offset))
  return params
}

/** Облачный чек из журнала продаж. */
interface CloudReceipt {
  id: string
  device_id: string
  total_amount: string
  /** Способ оплаты: 'cash' | 'card' | 'sbp' | null (чеки до 62). */
  payment_method: string | null
  /** Признак расчёта (ARCH-TASK-65): 'income' | 'income_return'. */
  settlement_type?: string | null
  /** Исходный чек возврата (ARCH-TASK-65.5); null — возврат без чека. */
  original_receipt_id?: string | null
  /** Фискальный статус (65.10): non_fiscal | marking_review | cancelled. */
  status?: string | null
  marking_skipped?: boolean
  marking_invalid?: boolean
  marking_reason?: string | null
  /** Статус сверки чека с ЧЗ (65.12.8): valid | invalid | unknown | null. */
  cz_status?: string | null
  /** 65.14: стадия печати hold-чека. */
  marking_print_state?: string | null
  marking_print_error?: string | null
  /** 65.14/9c: решение директора и его применение. */
  marking_decision?: 'approve' | 'cancel' | null
  marking_decision_source?: 'pos' | 'cloud' | 'auto' | null
  marking_cancel_mode?: 'refund' | 'writeoff' | null
  cashier_name?: string | null
  marking_decided_at?: string | null
  marking_applied_at?: string | null
  marking_apply_error?: string | null
  items: Array<{
    name?: string
    qty?: number
    quantity?: number
    price?: number
    marking_code?: string
  }>
  sold_at: string
  synced_at: string
}

/**
 * «Чеки в облаке»: журнал продаж с поиском по коду маркировки.
 * Живёт в разделе «Продажи»; без токена показывает подсказку.
 */
export function CloudReceiptsSection({
  refreshKey = 0,
  canApproveMarkingHold = false,
  canWriteoff = false
}: {
  refreshKey?: number
  canApproveMarkingHold?: boolean
  canWriteoff?: boolean
}) {
  const token = useAuthToken()
  const [markingCode, setMarkingCode] = useState('')
  const [appliedMarkingCode, setAppliedMarkingCode] = useState('')
  /** Фильтр «только возвраты» (ARCH-TASK-65.5). */
  const [returnsOnly, setReturnsOnly] = useState(false)
  /** 65.10: только нефискальные чеки (продажи без ККТ). */
  const [nonFiscalOnly, setNonFiscalOnly] = useState(false)
  /** 65.10: только продажи без кода маркировки. */
  const [markingSkippedOnly, setMarkingSkippedOnly] = useState(false)
  /** 65.12.8: статус сверки с ЧЗ ('' — все). */
  const [czStatus, setCzStatus] = useState('')
  /** 65.14/9d: фильтры решений и печати. */
  const [markingDecision, setMarkingDecision] = useState('')
  const [markingPrintState, setMarkingPrintState] = useState('')
  const [onlyApplyErrors, setOnlyApplyErrors] = useState(false)
  const [onlyNonFiscalMarked, setOnlyNonFiscalMarked] = useState(false)
  /** Раскрытые составы чеков (по id). */
  const [openItems, setOpenItems] = useState<Set<string>>(new Set())
  const [salesResult, setSalesResult] = useState<CloudReceipt[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [exportBusy, setExportBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  /** 65.14/9c: форма решения по hold-чеку. */
  const [decisionForm, setDecisionForm] = useState<{
    receipt: CloudReceipt
    decision: 'approve' | 'cancel'
    cancelMode: 'refund' | 'writeoff'
    comment: string
  } | null>(null)
  const [decisionBusy, setDecisionBusy] = useState(false)
  const [decisionError, setDecisionError] = useState<string | null>(null)
  /** 9d: ошибка ручного retry показывается вне модалки решения. */
  const [retryError, setRetryError] = useState<string | null>(null)
  /** Ручной refresh (поиск/повтор), не являющийся изменением фильтра. */
  const [refreshNonce, setRefreshNonce] = useState(0)
  /** Защита от гонки: применяем только ответ последнего запроса. */
  const requestIdRef = useRef(0)

  const filters: ReceiptFilters = {
    returnsOnly,
    nonFiscalOnly,
    markingSkippedOnly,
    czStatus,
    markingDecision,
    markingPrintState,
    onlyApplyErrors,
    onlyNonFiscalMarked
  }

  /** Загрузить журнал чеков из облака по текущим фильтрам. */
  const loadReceipts = useCallback(async () => {
    if (!token) {
      setSalesResult(null)
      return
    }
    const requestId = ++requestIdRef.current
    setBusy(true)
    setErr(null)
    setNote(null)
    setRetryError(null)
    try {
      const params = buildReceiptQuery(
        filters,
        appliedMarkingCode,
        RECEIPTS_PAGE_SIZE,
        0
      )
      const resp = await fetch(`${CLOUD_API}/receipts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (requestId !== requestIdRef.current) return
      if (!resp.ok) {
        setErr(httpErrorText('Запрос журнала', resp.status))
        return
      }
      const data = (await resp.json()) as { receipts: CloudReceipt[] }
      if (requestId !== requestIdRef.current) return
      setSalesResult(data.receipts)
      if (appliedMarkingCode.trim() && data.receipts.length === 0) {
        setNote('Чеки по запросу не найдены')
      }
    } catch (e2: unknown) {
      if (requestId === requestIdRef.current) {
        setErr(e2 instanceof Error ? e2.message : String(e2))
      }
    } finally {
      if (requestId === requestIdRef.current) setBusy(false)
    }
  }, [
    token,
    appliedMarkingCode,
    returnsOnly,
    nonFiscalOnly,
    markingSkippedOnly,
    czStatus,
    markingDecision,
    markingPrintState,
    onlyApplyErrors,
    onlyNonFiscalMarked
  ])

  // Единственная точка загрузки: фильтры, поиск и общая кнопка «Обновить».
  useEffect(() => {
    if (token) void loadReceipts()
  }, [token, loadReceipts, refreshKey, refreshNonce])

  const openDecision = (receipt: CloudReceipt, decision: 'approve' | 'cancel') => {
    setDecisionForm({ receipt, decision, cancelMode: 'refund', comment: '' })
    setDecisionError(null)
  }

  /** 65.14/9c: отправить решение директора в облако. */
  const submitDecision = async () => {
    if (!token || !decisionForm) return
    setDecisionBusy(true)
    setDecisionError(null)
    try {
      const body: Record<string, unknown> = {
        decision: decisionForm.decision
      }
      if (decisionForm.decision === 'cancel') {
        body.cancelMode = decisionForm.cancelMode
      }
      if (decisionForm.comment.trim()) body.comment = decisionForm.comment.trim()
      const resp = await fetch(
        `${CLOUD_API}/receipts/${encodeURIComponent(decisionForm.receipt.id)}/marking-decision`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        }
      )
      if (!resp.ok) {
        const data = (await resp.json().catch(() => null)) as { error?: string } | null
        setDecisionError(data?.error ?? httpErrorText('Решение', resp.status))
        return
      }
      setDecisionForm(null)
      setRefreshNonce((value) => value + 1)
    } catch (error) {
      setDecisionError(error instanceof Error ? error.message : String(error))
    } finally {
      setDecisionBusy(false)
    }
  }

  /** 9d: повторно отправить failed-решение на кассу. */
  const retryDecision = async (receiptId: string) => {
    if (!token) return
    setDecisionBusy(true)
    setRetryError(null)
    try {
      const resp = await fetch(
        `${CLOUD_API}/receipts/${encodeURIComponent(receiptId)}/marking-decision/retry`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      if (!resp.ok) {
        const data = (await resp.json().catch(() => null)) as { error?: string } | null
        setRetryError(data?.error ?? httpErrorText('Повтор решения', resp.status))
        return
      }
      setRefreshNonce((value) => value + 1)
    } catch (error) {
      setRetryError(error instanceof Error ? error.message : String(error))
    } finally {
      setDecisionBusy(false)
    }
  }

  const searchByMarking = (e: React.FormEvent) => {
    e.preventDefault()
    setAppliedMarkingCode(markingCode.trim())
    setRefreshNonce((value) => value + 1)
  }

  /** Сброс всех фильтров и поиска; загрузка пойдёт из useEffect один раз. */
  const resetSearch = () => {
    setMarkingCode('')
    setAppliedMarkingCode('')
    setReturnsOnly(false)
    setNonFiscalOnly(false)
    setMarkingSkippedOnly(false)
    setCzStatus('')
    setMarkingDecision('')
    setMarkingPrintState('')
    setOnlyApplyErrors(false)
    setOnlyNonFiscalMarked(false)
    setRetryError(null)
    setRefreshNonce((value) => value + 1)
  }

  /** 9d: Excel-выгрузка полной выборки (постранично), а не только 200 строк. */
  const exportReceipts = async () => {
    if (!token || !salesResult || salesResult.length === 0) return
    setExportBusy(true)
    setErr(null)
    try {
      const all: CloudReceipt[] = []
      let offset = 0
      let truncated = false
      while (true) {
        if (all.length >= RECEIPTS_EXPORT_MAX) {
          truncated = true
          break
        }
        const params = buildReceiptQuery(
          filters,
          appliedMarkingCode,
          RECEIPTS_PAGE_SIZE,
          offset
        )
        const resp = await fetch(`${CLOUD_API}/receipts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!resp.ok) {
          throw new Error(httpErrorText('Выгрузка чеков', resp.status))
        }
        const data = (await resp.json()) as { receipts: CloudReceipt[] }
        all.push(...data.receipts)
        if (data.receipts.length < RECEIPTS_PAGE_SIZE) break
        offset += RECEIPTS_PAGE_SIZE
      }

      const result = await exportWorkbook(`cheki_${new Date().toISOString().slice(0, 10)}.xlsx`, [
        {
          name: 'Чеки',
          columns: [
            { title: 'Чек', width: 38 },
            { title: 'Статус', width: 16 },
            { title: 'ЧЗ', width: 12 },
            { title: 'Источник решения', width: 18 },
            { title: 'Решение', width: 12 },
            { title: 'Режим отмены', width: 16 },
            { title: 'Печать', width: 14 },
            { title: 'Ошибка применения', width: 40 },
            { title: 'Сумма', width: 14 },
            { title: 'Кассир', width: 24 },
            { title: 'Продан', width: 20 }
          ],
          rows: all.map((r) => [
            r.id,
            r.status ?? '',
            r.cz_status ?? '',
            r.marking_decision_source ?? '',
            r.marking_decision ?? '',
            r.marking_cancel_mode ?? '',
            r.marking_print_state ?? '',
            r.marking_apply_error ?? '',
            Number(r.total_amount),
            r.cashier_name ?? '',
            new Date(r.sold_at).toLocaleString('ru-RU')
          ])
        }
      ])
      const suffix = truncated
        ? ` · выгружены первые ${all.length} из более чем ${RECEIPTS_EXPORT_MAX}`
        : ''
      setNote(
        result === 'saved-to-documents'
          ? `Файл сохранён в «Документах» приложения (${all.length} строк)${suffix}`
          : `Файл скачан (${all.length} строк)${suffix}`
      )
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error))
    } finally {
      setExportBusy(false)
    }
  }

  /** Сводка по загруженным чекам. */
  const salesSummary = salesResult
    ? {
        count: salesResult.length,
        total: salesResult.reduce((s, r) => s + Number(r.total_amount), 0)
      }
    : null

  /** Показать/скрыть состав чека. */
  const toggleItems = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div>
      {note && <p className={okBanner}>{note}</p>}
      {err && <p className={errBanner}>{err}</p>}
      {retryError && <p className={errBanner}>Повтор решения: {retryError}</p>}

      {!token ? (
        <p className="m-0 text-small text-muted">
          Подключите облако в разделе «Интеграции», чтобы видеть журнал продаж.
        </p>
      ) : (
        <>
          <form onSubmit={searchByMarking}>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  value={markingCode}
                  onChange={(e) => setMarkingCode(e.target.value)}
                  placeholder="Код марки DataMatrix (поиск по чекам)"
                  className={cx(credInput, 'w-full pr-9')}
                />
                {markingCode && (
                  <button
                    type="button"
                    onClick={resetSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-md px-1 text-small text-muted hover:text-ink"
                    aria-label="Сбросить поиск"
                    title="Сбросить поиск"
                  >
                    <Close size={16} />
                  </button>
                )}
              </div>
              <button type="submit" disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
                {busy ? 'Поиск…' : 'Найти'}
              </button>
              {/* Фильтр возвратов (ARCH-TASK-65.5): серверный, а не клиентский. */}
              <button
                type="button"
                aria-pressed={returnsOnly}
                onClick={() => setReturnsOnly((v) => !v)}
                disabled={busy}
                className={cx(
                  btnBase,
                  btnSize.sm,
                  returnsOnly
                    ? 'border-danger bg-danger text-white'
                    : btn.secondary
                )}
                title="Показать только возвраты"
              >
                Возвраты
              </button>
              {/* 65.10: только нефискальные чеки (продажи без ККТ). */}
              <button
                type="button"
                aria-pressed={nonFiscalOnly}
                onClick={() => setNonFiscalOnly((v) => !v)}
                disabled={busy}
                className={cx(
                  btnBase,
                  btnSize.sm,
                  nonFiscalOnly
                    ? 'border-amber-500 bg-amber-500 text-white'
                    : btn.secondary
                )}
                title="Показать только нефискальные чеки"
              >
                Без ККТ
              </button>
              <button
                type="button"
                aria-pressed={markingSkippedOnly}
                onClick={() => setMarkingSkippedOnly((v) => !v)}
                disabled={busy}
                className={cx(
                  btnBase,
                  btnSize.sm,
                  markingSkippedOnly
                    ? 'border-danger bg-danger text-white'
                    : btn.secondary
                )}
                title="Показать только продажи без кода маркировки"
              >
                Без кода
              </button>
              {/* 65.12.8: статус сверки чека с ЧЗ (серверный фильтр). */}
              <select
                value={czStatus}
                onChange={(e) => setCzStatus(e.target.value)}
                disabled={busy}
                className={cx(credInput, 'py-1 text-small')}
                title="Статус сверки чека с Честным Знаком"
              >
                <option value="">Статус Честного Знака: все</option>
                <option value="valid">Честный Знак: подтверждённые</option>
                <option value="invalid">Честный Знак: не подтверждённые</option>
                <option value="unknown">Честный Знак: не проверены</option>
              </select>
              <select
                value={markingDecision}
                onChange={(e) => setMarkingDecision(e.target.value)}
                disabled={busy}
                className={cx(credInput, 'py-1 text-small')}
                title="Решение по чеку на проверке"
              >
                <option value="">Решение: все</option>
                <option value="approve">Одобрено</option>
                <option value="cancel">Отменено</option>
              </select>
              <select
                value={markingPrintState}
                onChange={(e) => setMarkingPrintState(e.target.value)}
                disabled={busy}
                className={cx(credInput, 'py-1 text-small')}
                title="Стадия печати чека на проверке"
              >
                <option value="">Печать: все</option>
                <option value="ready">Готов к печати</option>
                <option value="failed">Ошибка печати</option>
                <option value="printed">Напечатан</option>
                <option value="cancelled">Отменён</option>
              </select>
              <button
                type="button"
                aria-pressed={onlyApplyErrors}
                onClick={() => setOnlyApplyErrors((value) => !value)}
                disabled={busy}
                className={cx(
                  btnBase,
                  btnSize.sm,
                  onlyApplyErrors ? 'border-danger bg-danger text-white' : btn.secondary
                )}
                title="Только ошибки применения решения"
              >
                Ошибки решения
              </button>
              <button
                type="button"
                aria-pressed={onlyNonFiscalMarked}
                onClick={() => setOnlyNonFiscalMarked((value) => !value)}
                disabled={busy}
                className={cx(
                  btnBase,
                  btnSize.sm,
                  onlyNonFiscalMarked ? 'border-amber-500 bg-amber-500 text-white' : btn.secondary
                )}
                title="Чеки без ККТ с кодом маркировки"
              >
                Без ККТ с кодом
              </button>
            </div>
          </form>

          <div className="my-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <strong className="tabular-nums">
                Всего загружено чеков: {salesSummary?.count ?? 0}
                {salesSummary ? ` · Общая сумма: ${money(salesSummary.total)}` : ''}
              </strong>
              {(salesSummary?.count ?? 0) >= RECEIPTS_PAGE_SIZE && (
                <span className="ml-2 text-tiny text-muted">
                  показаны первые {RECEIPTS_PAGE_SIZE}; Excel выгрузит всю выборку
                </span>
              )}
            </div>
            <button
              type="button"
              className={cx(btnBase, btn.secondary, btnSize.sm)}
              disabled={exportBusy || !salesResult?.length}
              onClick={() => void exportReceipts()}
            >
              {exportBusy ? 'Готовим файл…' : 'Выгрузить Excel'}
            </button>
          </div>

          {salesResult && salesResult.length > 0 ? (
            <div className={tableWrap}>
              <table className={table}>
                <thead>
                  <tr>
                    <th className={th}>Чек</th>
                    <th className={th}>Устройство</th>
                    <th className={th}>Оплата</th>
                    <th className={thNum}>Сумма</th>
                    <th className={th}>Состав</th>
                    <th className={th}>Продан</th>
                    <th className={th}>Решение</th>
                  </tr>
                </thead>
                <tbody>
                  {salesResult.map((r) => (
                    <tr key={r.id}>
                      <td className={cx(td, 'w-[110px] whitespace-nowrap font-mono text-tiny')}>
                        {r.id.slice(0, 8)}…
                        {r.settlement_type === 'income_return' && (
                          <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 font-sans text-tiny font-semibold text-dangerDark">
                            Возврат
                          </span>
                        )}
                        {r.settlement_type === 'income_return' && (
                          <span className="mt-0.5 block font-sans text-tiny text-muted">
                            {r.original_receipt_id
                              ? `по чеку ${r.original_receipt_id.slice(0, 8)}…`
                              : 'без исходного чека'}
                          </span>
                        )}
                        {r.status === 'non_fiscal' && (
                          <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 font-sans text-tiny font-semibold text-amber-800">
                            Без ККТ
                          </span>
                        )}
                        {r.status === 'voided' && (
                          <span
                            className="ml-1 rounded bg-soft px-1.5 py-0.5 font-sans text-tiny font-semibold text-muted"
                            title="Заменён фискальным чеком"
                          >
                            Заменён
                          </span>
                        )}
                        {r.marking_print_state && (
                          <span
                            className={cx(
                              'ml-1 rounded px-1.5 py-0.5 font-sans text-tiny font-semibold',
                              r.marking_print_state === 'printed'
                                ? 'bg-green-100 text-green-800'
                                : r.marking_print_state === 'failed'
                                  ? 'bg-red-100 text-dangerDark'
                                  : 'bg-soft text-muted'
                            )}
                            title={r.marking_print_error ?? 'Стадия печати чека на проверке'}
                          >
                            Печать: {PRINT_STATE_LABELS[r.marking_print_state] ?? r.marking_print_state}
                          </span>
                        )}
                        {r.marking_skipped && (
                          <span
                            className="ml-1 rounded bg-red-100 px-1.5 py-0.5 font-sans text-tiny font-semibold text-dangerDark"
                            title={r.marking_reason ?? undefined}
                          >
                            Без кода
                          </span>
                        )}
                        {r.cz_status && (
                          <span
                            className={cx(
                              'ml-1 rounded px-1.5 py-0.5 font-sans text-tiny font-semibold',
                              r.cz_status === 'valid'
                                ? 'bg-green-100 text-green-800'
                                : r.cz_status === 'invalid'
                                  ? 'bg-red-100 text-dangerDark'
                                  : 'bg-soft text-muted'
                            )}
                            title="Статус сверки чека с ЧЗ"
                          >
                            ЧЗ: {r.cz_status}
                          </span>
                        )}
                      </td>
                      <td className={cx(td, 'whitespace-nowrap')}>
                        {deviceLabel(r.device_id).label}
                      </td>
                      <td className={cx(td, 'whitespace-nowrap')}>
                        {paymentLabel(r.payment_method)}
                      </td>
                      <td
                        className={cx(
                          tdNum,
                          'whitespace-nowrap',
                          r.settlement_type === 'income_return' && 'text-dangerDark'
                        )}
                      >
                        {money(Number(r.total_amount))}
                      </td>
                      <td className={td}>
                        <button
                          type="button"
                          className="cursor-pointer text-small text-info hover:underline"
                          onClick={() => toggleItems(r.id)}
                          aria-expanded={openItems.has(r.id)}
                        >
                          <span>{r.items.length} поз.</span>
                          <ChevronDown
                            size={16}
                            className={openItems.has(r.id) ? 'rotate-180' : ''}
                          />
                        </button>
                        {openItems.has(r.id) && (
                          <div className="mt-1 text-small text-muted">
                            {r.items
                              .map((it) => `${it.name ?? '—'}×${it.quantity ?? it.qty ?? 1}`)
                              .join(', ')}
                          </div>
                        )}
                      </td>
                      <td className={cx(td, 'whitespace-nowrap')}>
                        {new Date(r.sold_at).toLocaleString('ru-RU')}
                      </td>
                      <td className={td}>
                        {r.marking_decision ? (
                          <span className="text-tiny text-muted">
                            {r.marking_decision === 'approve' ? 'Одобрен' : 'Отменён'}
                            {r.marking_applied_at ? ' · применён' : ''}
                            {r.marking_apply_error ? (
                              <span
                                className="mt-0.5 block text-dangerDark"
                                title={r.marking_apply_error}
                              >
                                Ошибка применения
                                {canApproveMarkingHold &&
                                  (r.marking_cancel_mode !== 'writeoff' || canWriteoff) && (
                                    <button
                                      type="button"
                                      className="ml-1 cursor-pointer underline"
                                      disabled={decisionBusy}
                                      onClick={() => void retryDecision(r.id)}
                                    >
                                      Повторить
                                    </button>
                                  )}
                              </span>
                            ) : null}
                          </span>
                        ) : r.status === 'marking_review' && canApproveMarkingHold ? (
                          <span className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              className={cx(btnBase, btn.success, btnSize.sm)}
                              disabled={r.cz_status !== 'valid'}
                              title={
                                r.cz_status === 'valid'
                                  ? 'Одобрить и пробить фискальный чек'
                                  : 'Одобрение доступно только для подтверждённых кодов'
                              }
                              onClick={() => openDecision(r, 'approve')}
                            >
                              Одобрить
                            </button>
                            <button
                              type="button"
                              className={cx(btnBase, btn.softDanger, btnSize.sm)}
                              onClick={() => openDecision(r, 'cancel')}
                            >
                              Отменить
                            </button>
                          </span>
                        ) : r.status === 'marking_review' ? (
                          <span className="text-tiny text-muted">Ожидает директора</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={hint}>Чеков пока нет.</p>
          )}
        </>
      )}

      {/* 65.14/9c: решение директора по hold-чеку. */}
      {decisionForm && (
        <div className={cx(overlay, 'z-modal')} role="dialog" aria-modal="true">
          <div className={modalSm}>
            <h3 className="mt-0 text-big font-bold">
              {decisionForm.decision === 'approve'
                ? 'Одобрить чек на проверке'
                : 'Отменить чек на проверке'}
            </h3>
            <p className={hint}>
              Чек {decisionForm.receipt.id.slice(0, 8)}… ·{' '}
              {money(Number(decisionForm.receipt.total_amount))}
              {decisionForm.decision === 'approve'
                ? '. Касса пробьёт фискальный чек при следующей синхронизации.'
                : '. Касса вернёт наличные и применит выбранный режим.'}
            </p>
            {decisionForm.decision === 'cancel' && (
              <label className={fieldLabel}>
                Режим отмены
                <select
                  className={fieldInput}
                  value={decisionForm.cancelMode}
                  onChange={(event) =>
                    setDecisionForm((current) =>
                      current
                        ? {
                            ...current,
                            cancelMode: event.target.value as 'refund' | 'writeoff'
                          }
                        : current
                    )
                  }
                >
                  <option value="refund">Вернуть товар на склад</option>
                  {canWriteoff && <option value="writeoff">Списать товар</option>}
                </select>
              </label>
            )}
            <label className={fieldLabel}>
              Комментарий
              <textarea
                className={fieldInput}
                rows={3}
                value={decisionForm.comment}
                onChange={(event) =>
                  setDecisionForm((current) =>
                    current ? { ...current, comment: event.target.value } : current
                  )
                }
                placeholder="Причина решения"
              />
            </label>
            {decisionError && <p className={errBanner}>{decisionError}</p>}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                className={cx(btnBase, btn.secondary, btnSize.md)}
                disabled={decisionBusy}
                onClick={() => setDecisionForm(null)}
              >
                Закрыть
              </button>
              <button
                type="button"
                className={cx(
                  btnBase,
                  decisionForm.decision === 'approve' ? btn.success : btn.danger,
                  btnSize.md
                )}
                disabled={decisionBusy}
                onClick={() => void submitDecision()}
              >
                {decisionBusy ? 'Отправка…' : 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
